import type { ButtonHTMLAttributes, CSSProperties } from "react";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost";

const VARIANTS: Record<Variant, string> = {
  // TA-1370 F-01: the hover fill is --color-brand-hover, never brand-700 (a light
  // tint in dark, 1.51:1 under on-brand). F-03: the transparent border is
  // invisible here and painted by forced colours, so the button keeps its edge.
  primary:
    "border border-transparent bg-[var(--color-brand-600)] text-[var(--color-on-brand)] hover:bg-[var(--color-brand-hover)]",
  secondary:
    "border border-[var(--color-border-strong)] bg-[var(--color-surface)] text-[var(--color-ink)] hover:bg-[var(--color-surface-subtle)]",
  ghost: "text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-subtle)]",
};

export function Button({
  variant = "secondary",
  className,
  busy,
  children,
  disabled,
  style,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  /**
   * TA-1322: the verb to show while the request this button sent is in
   * flight ("Approving", "Saving"). Set, the button is disabled, keeps the
   * idle label's width, shows the verb and a 2px accent segment sliding along
   * its bottom edge (globals.css .btn-working, the drafting bar's
   * ackra-slide). Static under reduced motion; the verb still shows.
   */
  busy?: string | false | null;
}) {
  const working = typeof busy === "string" && busy.length > 0;
  // Reserve the idle label's width (in ch, plus the px-3.5 padding) so the
  // button does not shrink when the verb is shorter than the label.
  const reserve: CSSProperties | undefined =
    working && typeof children === "string" ? { minWidth: `calc(${children.length}ch + 1.75rem)` } : undefined;
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center gap-1.5 rounded-[var(--radius-md)]",
        // TA-1318: a 36px target at 14px, and the primary label carries the
        // weight, so every form has one clear action.
        // TA-1319: .fade-disabled keeps the colour transition and eases the
        // disabled opacity in 120ms (globals.css, --duration-disabled).
        "min-h-9 px-3.5 py-2 text-[length:var(--text-sm)] fade-disabled",
        variant === "primary" ? "font-semibold" : "font-medium",
        // TA-1322: a working button is busy, not unavailable: full ink, the
        // progress cursor, and room for the segment along its bottom edge.
        working ? "relative overflow-hidden cursor-progress" : "disabled:cursor-not-allowed disabled:opacity-45",
        VARIANTS[variant],
        className,
      )}
      disabled={working || disabled}
      data-state={working ? "working" : undefined}
      aria-busy={working ? true : undefined}
      style={reserve ? { ...reserve, ...style } : style}
      {...props}
    >
      {working ? (
        <>
          <span>{busy}</span>
          <span aria-hidden="true" className="btn-working">
            <span />
          </span>
        </>
      ) : (
        children
      )}
    </button>
  );
}
